// A text's look as live's Parameters set it (#241 part 2): its box, face, size, alignment,
// decorations, case, colour, background, stroke and shadow, in live's pixels on live's frame
// (`LIVE_FRAME`). The timeline keeps it on each text; the engine draws it; the Inspector edits
// it through `styled`, which holds every value to the contract's range.
import type { FontFamily } from "../../api/generated/model";
import { nearestStyle } from "./fonts";
import {
  MAX_PLACE,
  clamp,
  colourOr,
  finite,
  keptShadow,
  keptStroke,
  type Shadow,
  type Stroke,
} from "./look";

/** A frame's size in live's pixels. */
interface Frame {
  width: number;
  height: number;
}

export type TextAlign = "left" | "center" | "right";
export type TextCase = "as_typed" | "upper" | "lower";

/** Live's Text Background: a box behind each line, padded, its corners rounded. */
export interface TextBackground {
  color: string;
  opacity: number;
  radius: number;
  padX: number;
  padY: number;
}

export interface TextStyle {
  /** The box's top left and width; its height is the text's own. */
  x: number;
  y: number;
  width: number;
  rotation: number;
  opacity: number;
  font: FontFamily;
  weight: number;
  italic: boolean;
  size: number;
  align: TextAlign;
  underline: boolean;
  overline: boolean;
  strikethrough: boolean;
  textCase: TextCase;
  /** Null is live's "−": the letters clear, their stroke and shadow still drawn. */
  color: string | null;
  background: TextBackground | null;
  stroke: Stroke | null;
  shadow: Shadow | null;
}

/** Live's padding inside a text's box, across and down, with no background (its Pad X, Pad Y). */
export const TEXT_PAD = { x: 8, y: 4 } as const;
/** Live's "Aa": from 8 px; the contract stops at 1000. */
export const MIN_TEXT_SIZE = 8;
export const MAX_TEXT_SIZE = 1000;
/** Live's line pitch: 1.15 of the text's size. */
export const LINE_PITCH = 1.15;
/** Live's "Add Text": its words, its width, and 5 s long. */
export const ADDED_TEXT = "New text element";
export const ADDED_TEXT_WIDTH = 590;
export const ADDED_TEXT_MS = 5000;

/** What Text Color's "+" and Text Background's "+" add (SPEC B5). */
export const ADDED_TEXT_COLOR = "#FFFFFF"; // raw-color: live's default
export const ADDED_BACKGROUND: TextBackground = {
  color: "#000000", // raw-color: live's default
  opacity: 1,
  radius: 4,
  padX: TEXT_PAD.x,
  padY: TEXT_PAD.y,
};

/**
 * Live's "Add Text" on `frame` (live's pixels): Roboto Bold 48, white, centred, 590 wide,
 * placed in the middle of the frame (its height taken as one line, as live's first guess).
 */
export function addedStyle(frame: Frame): TextStyle {
  const size = 48;
  return {
    x: Math.round((frame.width - ADDED_TEXT_WIDTH) / 2),
    y: Math.round((frame.height - size * 1.2) / 2),
    width: ADDED_TEXT_WIDTH,
    rotation: 0,
    opacity: 1,
    font: "Roboto",
    weight: 700,
    italic: false,
    size,
    align: "center",
    underline: false,
    overline: false,
    strikethrough: false,
    textCase: "as_typed",
    color: ADDED_TEXT_COLOR,
    background: null,
    stroke: null,
    shadow: null,
  };
}

/** Where E1's titles sat: their middle as a share of the frame's height. */
const TITLE_Y = { top: 0.08, center: 0.5, bottom: 0.92 } as const;
const TITLE_VEIL = "#000000"; // raw-color: E1's title box

/**
 * A title made before #241 part 2 (a `position` and no style) as live's text that looks as E1
 * drew it: Inter Bold at 6 % of the frame's short side, white on a dark box across 88 % of it,
 * centred at its place.
 */
export function titleStyle(position: keyof typeof TITLE_Y, frame: Frame): TextStyle {
  const size = Math.round(Math.min(frame.width, frame.height) * 0.06);
  const width = Math.round(frame.width * 0.88);
  const height = Math.round(size * LINE_PITCH + size * 0.4);
  return {
    ...addedStyle(frame),
    x: Math.round((frame.width - width) / 2),
    y: Math.round(Math.min(Math.max(0, frame.height * TITLE_Y[position] - height / 2), frame.height - height)),
    width,
    font: "Inter",
    size,
    background: {
      color: TITLE_VEIL,
      opacity: 0.68,
      radius: Math.round(size / 6),
      padX: Math.round(size * 0.5),
      padY: Math.round(size * 0.2),
    },
  };
}

/** What `styled` takes: any of the style's values, a background, stroke or shadow whole or null. */
export type TextStylePatch = Partial<TextStyle>;


/**
 * The style with `patch` applied, each value held to the contract's range: places to ±10000 and
 * tenths, a width from 1, sizes 8 to 1000 whole, a weight the family has (a new family keeps the
 * nearest), opacities 0 to 1, padding and corners 0 to 500, colours `#RRGGBB` (another keeps
 * the last good one).
 */
export function styled(style: TextStyle, patch: TextStylePatch): TextStyle {
  const place = (next: number | undefined, current: number, low = -MAX_PLACE) =>
    clamp(Math.round(finite(next, current) * 10) / 10, low, MAX_PLACE);
  const font = patch.font ?? style.font;
  const face = nearestStyle(font, finite(patch.weight, style.weight), patch.italic ?? style.italic);
  const background =
    patch.background === undefined
      ? style.background
      : patch.background === null
        ? null
        : {
            color: colourOr(patch.background.color, style.background?.color ?? ADDED_BACKGROUND.color),
            opacity: clamp(finite(patch.background.opacity, 1), 0, 1),
            radius: clamp(finite(patch.background.radius, 0), 0, 500),
            padX: clamp(finite(patch.background.padX, 0), 0, 500),
            padY: clamp(finite(patch.background.padY, 0), 0, 500),
          };
  return {
    x: place(patch.x, style.x),
    y: place(patch.y, style.y),
    width: place(patch.width, style.width, 1),
    rotation: clamp(Math.round(finite(patch.rotation, style.rotation) * 10) / 10, -360, 360),
    opacity: clamp(finite(patch.opacity, style.opacity), 0, 1),
    font,
    weight: face.weight,
    italic: face.italic,
    size: clamp(Math.round(finite(patch.size, style.size)), MIN_TEXT_SIZE, MAX_TEXT_SIZE),
    align: patch.align ?? style.align,
    underline: patch.underline ?? style.underline,
    overline: patch.overline ?? style.overline,
    strikethrough: patch.strikethrough ?? style.strikethrough,
    textCase: patch.textCase ?? style.textCase,
    color:
      patch.color === undefined
        ? style.color
        : patch.color === null
          ? null
          : colourOr(patch.color, style.color ?? ADDED_TEXT_COLOR),
    background,
    stroke: keptStroke(patch.stroke, style.stroke),
    shadow: keptShadow(patch.shadow, style.shadow),
  };
}

/** The text as drawn: live's case applied (the stored text keeps what was typed). */
export function shownText(text: string, textCase: TextCase): string {
  if (textCase === "upper") return text.toUpperCase();
  if (textCase === "lower") return text.toLowerCase();
  return text;
}

/** The padding inside the box: the background's, or live's 8 and 4 with none. */
export function paddingOf(style: TextStyle): { x: number; y: number } {
  return style.background === null
    ? TEXT_PAD
    : { x: style.background.padX, y: style.background.padY };
}
